import _ from 'lodash';
import SoqlDataProvider from '../dataProviders/SoqlDataProvider';
import SoqlHelpers from '../helpers/SoqlHelpers';
import { I18n } from 'common/i18n';
import { createDuplicateValuesError, createMaxRowCountError } from './DataManagerError';

function makeSocrataCategoricalDataRequest(vif, seriesIndex, maxRowCount) {
  const series = vif.series[seriesIndex];
  const requireGroupingInSelect = vif.requireGroupingInSelect;
  const queryClauses = [];
  const isAggregated =
    !_.isNil(_.get(series, 'dataSource.dimension.aggregationFunction')) ||
    !_.isNil(_.get(series, 'dataSource.measure.aggregationFunction'));

  const fields = [
    `${SoqlHelpers.dimension(vif, seriesIndex)} AS ${SoqlHelpers.dimensionAlias()}`,
    `${SoqlHelpers.measure(vif, seriesIndex)} AS ${SoqlHelpers.measureAlias()}`,
  ];

  if (requireGroupingInSelect) {
    // We still add the grouping column even if there's no aggregation.
    // This will become a plain select query, and later on we'll see if the dimension is actually unique.
    fields.push(`${SoqlHelpers.grouping(vif, seriesIndex)} AS ${SoqlHelpers.groupingAlias()}`);
  }

  const errorBarsLower = SoqlHelpers.errorBarsLower(vif, seriesIndex);
  const errorBarsUpper = SoqlHelpers.errorBarsUpper(vif, seriesIndex);
  const haveErrorBars = !_.isEmpty(errorBarsLower) && !_.isEmpty(errorBarsUpper);
  if (haveErrorBars) {
    fields.push(
      `${errorBarsLower} AS ${SoqlHelpers.errorBarsLowerAlias()}`,
      `${errorBarsUpper} AS ${SoqlHelpers.errorBarsUpperAlias()}`,
    );
  }

  queryClauses.push('SELECT', fields.join(', '));

  const whereClauseComponents = SoqlHelpers.whereClauseFilteringOwnColumn(vif, seriesIndex);
  if (whereClauseComponents.length > 0) {
    queryClauses.push(`WHERE ${whereClauseComponents}`);
  }

  // No group by if there's no aggregation.
  if (isAggregated) {
    let groupByString = `GROUP BY ${SoqlHelpers.aggregationClause(vif, seriesIndex, 'dimension')}`;
    if (requireGroupingInSelect) {
      groupByString += `, ${SoqlHelpers.groupingAlias()}`;
    }
    queryClauses.push(groupByString);
  }

  queryClauses.push(`ORDER BY ${SoqlHelpers.orderByClauseFromSeries(vif, seriesIndex)}`);
  queryClauses.push('NULL LAST');

  /*
   * I'll add some context here for something that may not be obvious.
   * Notice that we don't check whether we needed more than `LIMIT` rows to satisfy a query?
   * Doesn't that mean some categorical vizes will have some inaccurate data?
   * Technically, yes!
   * But to see it, you would need a grouping with ~100+ unique values, and either scroll to the bottom
   * of a very long viz, or sort the viz backwards, so you see the smallest groups first.
   * Both of those make no sense, and users never (no seriously, never) want to see that data in a viz.
   *
   * Such a check would also be very annoying, because it would warn you about using too many rows
   * far too often, for vizes that work fine as long as you don't scroll really far down.
   * This could work more intelligently in a lot of ways, but <insert all existing issues with this code>.
   */
  const maybeNaNLimit = parseInt(_.get(vif, `series[${seriesIndex}].dataSource.limit`), 10);
  const limitFromVif = Number.isFinite(maybeNaNLimit) ? maybeNaNLimit : undefined;
  const showOtherCategory = limitFromVif && _.get(vif, 'configuration.showOtherCategory', false);
  const isTimelineChart =
    _.get(vif, 'visualizationType') === 'timeline' ||
    _.get(vif, `series[${seriesIndex}].type`) === 'timelineChart';

  function getLimit() {
    // For showOtherCategory, we don't use the `limitFromVif` in the query, we just use the `maxRowCount`.
    // We'll truncate to `limitFromVif` in the response handler.
    if (showOtherCategory) {
      return maxRowCount;
    }

    /*
     * Timeline charts only hit the CategoricalDataManager in a special case.
     * When the "Time Grouping" aka `dataSource.precision` is "none", the query doesn't need any time buckets.
     * Each day/hour etc. is one category/group.
     * So we switch off of TimeDataManager further up, currently SvgTimelineChart.js#L174
     *
     * Timeline charts also DO need to check for using row count, because they show so much more data and its
     * extremely easy to try to make a timeline chart that uses too many rows.
     * The way we do the check is to ask for limit+=1 rows, so we can compare against the real limit later.
     * This limit+=1 trick is the same one used in makeSocrataTimeDataRequest, and is also used
     * in GroupedCategoricalDataManager whenever we need to check required rows against a limit.
     */
    if (isTimelineChart) {
      return maxRowCount + 1;
    }

    if (limitFromVif) {
      return limitFromVif;
    }

    return maxRowCount;
  }

  queryClauses.push(`LIMIT ${getLimit()}`);

  const queryString = queryClauses.join(' ');

  const soqlDataProvider = new SoqlDataProvider(
    {
      datasetUid: series.dataSource.datasetUid,
      domain: series.dataSource.domain,
      clientContextVariables: series.dataSource.parameterOverrides,
    },
    true,
  );

  return soqlDataProvider
    .query(
      queryString,
      SoqlHelpers.dimensionAlias(),
      SoqlHelpers.measureAlias(),
      haveErrorBars ? SoqlHelpers.errorBarsLowerAlias() : null,
      haveErrorBars ? SoqlHelpers.errorBarsUpperAlias() : null,
      requireGroupingInSelect ? SoqlHelpers.groupingAlias() : null,
    )
    .then(checkMaxRowCount(maxRowCount, isTimelineChart))
    .then(checkForDuplicateValues(isAggregated, requireGroupingInSelect))
    .then(aggregateOtherCategory(showOtherCategory, limitFromVif, haveErrorBars))
    .then(mapQueryResponseToDataTable(vif));
}

function checkMaxRowCount(maxRowCount, isTimelineChart) {
  return (queryResponse) => {
    // For timeline charts only
    if (isTimelineChart && queryResponse.rows.length > maxRowCount) {
      throw createMaxRowCountError(maxRowCount);
    }

    return queryResponse;
  };
}

function checkForDuplicateValues(isAggregated, requireGroupingInSelect) {
  return (queryResponse) => {
    const rowCount = queryResponse.rows.length;
    const dimensionCount = _.uniq(queryResponse.rows.map((row) => row[0])).length;
    const groupCount = _.uniq(queryResponse.rows.map((row) => row[1])).length;
    if (!requireGroupingInSelect && rowCount !== dimensionCount) {
      // Queries that don't do grouping should have exactly one row per dimension.
      throw createDuplicateValuesError();
    }
    if (requireGroupingInSelect && !isAggregated) {
      // Special support for queries that group by a column but have aggregate = None
      // It's possible that the dimension/group pairs have unique values even if the dimension itself doesn't.
      // We can start by checking whether there are more rows than possible dimension/group pairs.
      if (dimensionCount * groupCount < rowCount) {
        throw createDuplicateValuesError();
      }

      // Now we need to go one by one and check that each dimension has a unique set of groups.
      // lets make a hashmap between dim names and a set of group names
      const dimToGroupMap = {};
      queryResponse.rows.forEach(([dim, group, value]) => {
        if (!dimToGroupMap[dim]) {
          // dim is new
          dimToGroupMap[dim] = new Set();
          dimToGroupMap[dim].add(group);
        } else if (!dimToGroupMap[dim].has(group)) {
          // group is new
          dimToGroupMap[dim].add(group);
        } else {
          // group is not new
          throw createDuplicateValuesError();
        }
      });
    }
    return queryResponse;
  };
}

function aggregateOtherCategory(showOtherCategory, limitFromVif, haveErrorBars) {
  return (queryResponse) => {
    const rowCount = queryResponse.rows.length;
    delete queryResponse.rowIds;

    // Main functionality of showOtherCategory is here. We want to keep only limitFromVif rows,
    // and add an "Other" row to the end of the data table that aggregates the remaining rows.
    if (showOtherCategory && rowCount > limitFromVif) {
      const otherLabel = I18n.t('shared.visualizations.charts.common.other_category');
      const otherRows = queryResponse.rows.slice(limitFromVif);
      queryResponse.rows = queryResponse.rows.slice(0, limitFromVif);

      // treat undefined (where measure is null) as 0 while calculating the sum so it does not result in NaN
      const otherTotal = _.sumBy(otherRows, (row) => {
        const rowValue = Number.parseInt(row[1], 10);
        return Number.isNaN(rowValue) ? 0 : rowValue;
      });
      queryResponse.rows.push([otherLabel, otherTotal]);

      // Error bars for (Other) category.
      // I don't think this calculation existed before, it was just left empty.
      if (haveErrorBars) {
        const otherErrorBars = queryResponse.errorBars.slice(limitFromVif);
        queryResponse.errorBars = queryResponse.errorBars.slice(0, limitFromVif);
        const lowerTotal = _.sumBy(otherErrorBars, (bar) => parseInt(bar[1], 10));
        const upperTotal = _.sumBy(otherErrorBars, (bar) => parseInt(bar[2], 10));
        queryResponse.errorBars.push([otherLabel, lowerTotal, upperTotal]);
      }
    }

    return Promise.resolve(queryResponse);
  };
}

function mapQueryResponseToDataTable(vif) {
  return (queryResponse) => {
    const dataTable = queryResponse;
    const dimensionIndex = dataTable.columns.indexOf(SoqlHelpers.dimensionAlias());
    const groupingIndex = dataTable.columns.indexOf(SoqlHelpers.groupingAlias());
    const measureIndex = dataTable.columns.indexOf(SoqlHelpers.measureAlias());

    dataTable.columns[dimensionIndex] = 'dimension';
    dataTable.columns[measureIndex] = 'measure';

    if (groupingIndex !== -1) {
      dataTable.columns[groupingIndex] = 'grouping';
    }

    const treatNullValuesAsZero = _.get(vif, 'configuration.treatNullValuesAsZero', false);

    dataTable.rows.forEach((row) => {
      if (_.isUndefined(row[dimensionIndex])) {
        row[dimensionIndex] = null;
      }

      if (groupingIndex !== -1) {
        if (_.isUndefined(row[groupingIndex])) {
          row[groupingIndex] = null;
        }
      }

      if (_.isNil(row[measureIndex])) {
        row[measureIndex] = treatNullValuesAsZero ? 0 : null;
      } else {
        row[measureIndex] = getValue(row[measureIndex]);
      }
    });

    if (!_.isUndefined(dataTable.errorBars)) {
      dataTable.errorBars = dataTable.errorBars.map((row) => {
        const dimensionIndex = 0;
        const lowerBoundIndex = 1;
        const upperBoundIndex = 2;
        const newRow = [];

        // Dimension
        newRow[dimensionIndex] = _.isUndefined(row[dimensionIndex]) ? null : row[dimensionIndex];

        // Error bar bounds
        newRow[measureIndex] = [getValue(row[lowerBoundIndex]), getValue(row[upperBoundIndex])];

        return newRow;
      });
    }

    return dataTable;
  };
}

function getValue(o) {
  if (_.isNil(o)) {
    return null;
  } else if (typeof o !== 'boolean' && window.isFinite(o)) {
    // Do an explicit check for boolean here because Number(true)
    // returns 1, and we want bools to remain bools.
    return Number(o);
  } else {
    return o;
  }
}

export default makeSocrataCategoricalDataRequest;
